import React from 'react';
import uuid from 'uuid';
import { isFedRamp } from '@atlassian/atl-context';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { useProjectKey, useIssueId, useIssueKey } from '@atlassian/jira-issue-context-service';
import { useFieldValue } from '@atlassian/jira-issue-field-base';
import { useIsIssueOfIncidentsPractice } from '@atlassian/jira-issue-field-servicedesk-practices';
import { SIMILAR_ISSUES_TYPE } from '@atlassian/jira-platform-field-config';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { useProjectId, useProjectStatus } from '@atlassian/jira-project-context-service';
import { PROJECT_ARCHIVED } from '@atlassian/jira-project-context-service/src/constants';
import {
	toIssueId,
	toProjectId,
	type BaseUrl,
	type CloudId,
	type IssueId,
	type ProjectId,
	type ProjectKey,
	toBaseUrl,
} from '@atlassian/jira-shared-types';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';

import { RelatedIssuesForIssueContainer } from '../services';
import RelatedIssuesPanelView from './main';
import SimilarIncidentsPanelView from './similar-incidents';

const hasValue = (x?: null | string | IssueId): x is string | IssueId =>
	x !== null && x !== undefined;

const hasStringValue = (str?: string | null): str is string => hasValue(str) && str !== '';

const isRequiredAttributesPresent = (
	issueId: IssueId | null | undefined,
	projectId: ProjectId | null | undefined,
	projectKey: ProjectKey,
	baseUrl: BaseUrl,
	cloudId: CloudId,
) => {
	if (
		hasStringValue(projectId) &&
		projectId !== toProjectId('') &&
		hasValue(issueId) &&
		issueId !== toIssueId('') &&
		hasStringValue(cloudId)
	) {
		return {
			issueId,
			projectId,
			projectKey,
			baseUrl,
			cloudId,
		};
	}

	return undefined;
};

const RelatedIssuesPanel = () => {
	const sessionId = uuid.v4();
	const issueId = useIssueId();
	const projectKey = useProjectKey();
	const projectId = toProjectId(useProjectId(projectKey) || '');
	const projectStatus = useProjectStatus(projectKey);
	const cloudId = useCloudId();
	const baseUrl = toBaseUrl('');
	const issueKey = useIssueKey();
	const [similarIssuesTypeFieldValue] = useFieldValue({
		issueKey,
		fieldKey: SIMILAR_ISSUES_TYPE,
	});
	const isIssueOfIncidentPractice = useIsIssueOfIncidentsPractice();

	const validatedProps = isRequiredAttributesPresent(
		issueId,
		projectId,
		projectKey,
		baseUrl,
		cloudId,
	);

	const isProjectArchived = projectStatus === PROJECT_ARCHIVED;

	return !!similarIssuesTypeFieldValue &&
		validatedProps !== undefined &&
		!isProjectArchived &&
		!isFedRamp() ? (
		<JSErrorBoundary
			id="relatedIssuesPanel"
			packageName="issueSmartRelatedIssuesPanel"
			fallback="unmount"
			teamName="itsm-grimlock"
		>
			<RelatedIssuesForIssueContainer {...validatedProps}>
				<ContextualAnalyticsData
					sourceName="RelatedIssuesPanel"
					attributes={{
						smartSessionId: sessionId,
					}}
				>
					{isIssueOfIncidentPractice ? (
						<SimilarIncidentsPanelView sessionId={sessionId} />
					) : (
						<RelatedIssuesPanelView sessionId={sessionId} />
					)}
				</ContextualAnalyticsData>
			</RelatedIssuesForIssueContainer>
		</JSErrorBoundary>
	) : null;
};

export default RelatedIssuesPanel;
